import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  UserGroupIcon, 
  ChartBarIcon, 
  BoltIcon,
  CircleStackIcon,
  SignalIcon,
  CloudIcon,
  BellAlertIcon,
  WifiIcon,
  ClockIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  GlobeAltIcon,
  DocumentChartBarIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import DeviceIcon from '../components/DeviceIcon';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { format, subMinutes, subHours } from 'date-fns';
import SystemHealth from '../components/SystemHealth';
import SystemHealthDetail from '../components/SystemHealthDetail';
import RecentAlarms from '../components/RecentAlarms';

// Main stat card with navigation
const MainStatCard = ({ title, value, subValue, icon: Icon, color = 'blue', onClick, delay = 0 }) => {
  const colors = {
    blue: {
      bg: 'bg-blue-50 hover:bg-blue-100',
      icon: 'text-blue-600',
      iconBg: 'bg-blue-100',
      text: 'text-blue-900'
    },
    green: {
      bg: 'bg-green-50 hover:bg-green-100',
      icon: 'text-green-600',
      iconBg: 'bg-green-100',
      text: 'text-green-900'
    },
    purple: {
      bg: 'bg-purple-50 hover:bg-purple-100',
      icon: 'text-purple-600',
      iconBg: 'bg-purple-100',
      text: 'text-purple-900'
    },
    yellow: {
      bg: 'bg-yellow-50 hover:bg-yellow-100',
      icon: 'text-yellow-600',
      iconBg: 'bg-yellow-100',
      text: 'text-yellow-900'
    },
    red: {
      bg: 'bg-red-50 hover:bg-red-100',
      icon: 'text-red-600',
      iconBg: 'bg-red-100',
      text: 'text-red-900'
    }
  };
  
  const colorScheme = colors[color] || colors.blue;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ scale: 1.02 }}
      onClick={onClick}
      className={`${colorScheme.bg} rounded-xl p-6 cursor-pointer transition-all border border-transparent hover:border-gray-200 hover:shadow-lg`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 ${colorScheme.iconBg} rounded-lg`}>
          <Icon className={`w-8 h-8 ${colorScheme.icon}`} />
        </div>
      </div>
      
      <div>
        <p className="text-gray-600 text-sm font-medium mb-2">{title}</p>
        <h3 className={`text-4xl font-bold ${colorScheme.text}`}>{value}</h3>
        {subValue && (
          <p className="text-sm text-gray-600 mt-2">{subValue}</p>
        )}
      </div>
    </motion.div>
  );
};

// Quick stat card
const QuickStat = ({ label, value, icon: Icon, color = 'gray' }) => {
  const colors = {
    gray: 'text-gray-600 bg-gray-100',
    green: 'text-green-600 bg-green-100',
    blue: 'text-blue-600 bg-blue-100',
    red: 'text-red-600 bg-red-100',
    yellow: 'text-yellow-600 bg-yellow-100'
  };
  
  return (
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-semibold text-gray-900">{value}</p>
      </div>
    </div>
  );
};

// Alert item
const AlertItem = ({ type, message, time, device }) => {
  const typeConfig = {
    alarm: { icon: BellAlertIcon, color: 'text-red-500', bg: 'bg-red-50' },
    connection: { icon: WifiIcon, color: 'text-blue-500', bg: 'bg-blue-50' },
    warning: { icon: ExclamationCircleIcon, color: 'text-yellow-500', bg: 'bg-yellow-50' },
    success: { icon: CheckCircleIcon, color: 'text-green-500', bg: 'bg-green-50' }
  };
  
  const config = typeConfig[type] || typeConfig.warning;
  const Icon = config.icon;
  
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg ${config.bg}`}>
      <Icon className={`w-5 h-5 ${config.color} flex-shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 font-medium">{message}</p>
        {device && (
          <p className="text-xs text-gray-600 mt-1">Device: {device}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">{format(new Date(time), 'HH:mm:ss')}</p>
      </div>
    </div>
  );
};

const OverviewRedesigned = ({ systemStats, eventHistory = [] }) => {
  const navigate = useNavigate();
  const [showSystemHealthDetail, setShowSystemHealthDetail] = useState(false);

  // Calculate real-time metrics
  const metrics = useMemo(() => {
    const now = new Date();
    const oneHourAgo = subHours(now, 1);
    const fiveMinutesAgo = subMinutes(now, 5);
    
    // Recent events
    const recentEvents = eventHistory.filter(e => new Date(e.timestamp) >= fiveMinutesAgo);
    const hourlyEvents = eventHistory.filter(e => new Date(e.timestamp) >= oneHourAgo);
    
    // Device metrics
    const deviceConnections = hourlyEvents.filter(e => e.type === 'device_connected').length;
    const deviceDisconnections = hourlyEvents.filter(e => e.type === 'device_disconnected').length;
    const deviceTrend = deviceConnections - deviceDisconnections;
    
    // User metrics
    const userConnections = hourlyEvents.filter(e => e.type === 'subscriber_connected').length;
    const userDisconnections = hourlyEvents.filter(e => e.type === 'subscriber_disconnected').length;
    const userTrend = userConnections - userDisconnections;
    
    // Event rate
    const eventRate = recentEvents.length / 5; // Events per minute
    
    return {
      deviceTrend: deviceTrend > 0 ? '+' + deviceTrend : deviceTrend.toString(),
      userTrend: userTrend > 0 ? '+' + userTrend : userTrend.toString(),
      eventRate: eventRate.toFixed(1),
      recentActivity: recentEvents.length
    };
  }, [eventHistory]);

  // Generate time series data for charts
  const timeSeriesData = useMemo(() => {
    const data = [];
    const now = new Date();
    
    // Create 30-minute time buckets
    for (let i = 29; i >= 0; i--) {
      const bucketTime = subMinutes(now, i);
      const bucketStart = subMinutes(now, i + 1);
      
      const bucketEvents = eventHistory.filter(event => {
        const eventTime = new Date(event.timestamp);
        return eventTime >= bucketStart && eventTime < bucketTime;
      });
      
      const deviceEvents = bucketEvents.filter(e => 
        e.type === 'device_connected' || e.type === 'device_disconnected'
      ).length;
      
      const userEvents = bucketEvents.filter(e => 
        e.type === 'subscriber_connected' || e.type === 'subscriber_disconnected'
      ).length;
      
      const dataEvents = bucketEvents.filter(e => 
        e.type === 'data_forwarded' || e.type === 'telemetry' || e.type === 'stream'
      ).length;
      
      data.push({
        time: format(bucketTime, 'HH:mm'),
        devices: deviceEvents,
        users: userEvents,
        data: dataEvents,
        total: bucketEvents.length
      });
    }
    
    return data;
  }, [eventHistory]);

  // Get recent alerts
  const recentAlerts = useMemo(() => {
    const alertEvents = eventHistory
      .filter(e => 
        e.type === 'alarm_new' || 
        e.type === 'alarm_cleared' || 
        e.type === 'device_disconnected' ||
        e.type === 'subscriber_connected'
      )
      .slice(0, 5)
      .map(e => {
        let type, message;
        
        switch (e.type) {
          case 'alarm_new':
            type = 'alarm';
            message = `New ${e.severity || 'alarm'} alarm: ${e.message || 'Unknown'}`;
            break;
          case 'alarm_cleared':
            type = 'success';
            message = `Alarm cleared: ${e.message || 'Unknown'}`;
            break;
          case 'device_disconnected':
            type = 'connection';
            message = `Device disconnected`;
            break;
          case 'subscriber_connected':
            type = 'connection';
            message = `User connected from ${e.ip || 'unknown IP'}`;
            break;
          default:
            type = 'warning';
            message = e.message || 'Unknown event';
        }
        
        return {
          id: e.id,
          type,
          message,
          time: e.timestamp,
          device: e.serial
        };
      });
    
    return alertEvents;
  }, [eventHistory]);

  // Device distribution data for pie chart
  const deviceDistribution = useMemo(() => {
    const connected = systemStats?.devicesConnected || 0;
    const total = systemStats?.totalDevices || connected;
    const offline = Math.max(0, total - connected);
    
    return [
      { name: 'Online', value: connected, color: '#10B981' },
      { name: 'Offline', value: offline, color: '#EF4444' }
    ];
  }, [systemStats]);

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <MainStatCard
          title="Connected Devices"
          value={systemStats?.devicesConnected || 0}
          subValue={`${metrics.deviceTrend} in last hour`}
          icon={DeviceIcon}
          color="blue"
          onClick={() => navigate('/dashboard/devices')}
          delay={0.1}
        />
        <MainStatCard
          title="Active Users"
          value={systemStats?.subscribersConnected || 0}
          subValue={`${systemStats?.usersConnected ?? 0} users · ${systemStats?.employeesConnected ?? 0} employees`}
          icon={UserGroupIcon}
          color="green"
          onClick={() => navigate('/dashboard/users')}
          delay={0.2}
        />
        <MainStatCard
          title="Event Rate"
          value={`${metrics.eventRate}/min`}
          subValue={`${metrics.recentActivity} in last 5 min`}
          icon={ChartBarIcon}
          color="purple"
          onClick={() => navigate('/dashboard/events')}
          delay={0.4}
        />
      </div>

      {/* Recent Alarms */}
      <RecentAlarms />

      {/* Second Row: System Health and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* System Health */}
        <div className="lg:col-span-1">
          <SystemHealth onExpand={() => setShowSystemHealthDetail(true)} />
        </div>

        {/* Activity Timeline */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Activity Timeline</h3>
            <select className="text-sm border border-gray-300 rounded-lg px-3 py-1">
              <option>Last 30 minutes</option>
              <option>Last hour</option>
              <option>Last 6 hours</option>
            </select>
          </div>
          
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={timeSeriesData}>
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="time" 
                stroke="#6B7280" 
                fontSize={12}
                interval="preserveStartEnd"
              />
              <YAxis stroke="#6B7280" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="total" 
                stroke="#8B5CF6" 
                fillOpacity={1}
                fill="url(#colorTotal)" 
                strokeWidth={2}
                name="Total Events"
              />
              <Line 
                type="monotone" 
                dataKey="devices" 
                stroke="#3B82F6" 
                strokeWidth={2}
                dot={false}
                name="Device Events"
              />
              <Line 
                type="monotone" 
                dataKey="users" 
                stroke="#10B981" 
                strokeWidth={2}
                dot={false}
                name="User Events"
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Third Row: Device Distribution and Recent Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Device Distribution */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Status</h3>
          <div className="flex items-center justify-center">
            <ResponsiveContainer width={200} height={200}>
              <PieChart>
                <Pie
                  data={deviceDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {deviceDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4">
            {deviceDistribution.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm text-gray-600">
                  {item.name}: {item.value}
                </span>
              </div>
            ))}
          </div>
          
          {/* Quick Device Stats */}
          <div className="grid grid-cols-2 gap-4 mt-6">
            <QuickStat 
              label="Response Time" 
              value="45ms" 
              icon={ClockIcon}
              color="blue"
            />
            <QuickStat 
              label="Data Rate" 
              value="2.4MB/s" 
              icon={ArrowTrendingUpIcon}
              color="green"
            />
          </div>
        </motion.div>

        {/* Recent Alerts */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <button 
              onClick={() => navigate('/dashboard/events')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              View All →
            </button>
          </div>
          
          <div className="space-y-3">
            {recentAlerts.length > 0 ? (
              recentAlerts.map((alert) => (
                <AlertItem key={alert.id} {...alert} />
              ))
            ) : (
              <div className="text-center py-8">
                <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto" />
                <p className="mt-2 text-gray-500">No recent alerts</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Fourth Row: Infrastructure Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200"
      >
        <div className="flex items-center gap-3 mb-6">
          <GlobeAltIcon className="w-6 h-6 text-gray-700" />
          <h3 className="text-lg font-semibold text-gray-900">Infrastructure Overview</h3>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <QuickStat label="AWS Region" value="us-east-1" icon={CloudIcon} color="blue" />
          <QuickStat label="Database" value="MongoDB" icon={CircleStackIcon} color="green" />
          <QuickStat label="WebSockets" value="Active" icon={WifiIcon} color="green" />
          <QuickStat label="API Latency" value="125ms" icon={SignalIcon} color="yellow" />
          <QuickStat label="SSL Status" value="Valid" icon={CheckCircleIcon} color="green" />
          <QuickStat label="Uptime" value="99.9%" icon={ArrowTrendingUpIcon} color="green" />
        </div>
      </motion.div>

      {/* System Health Detail Modal */}
      <SystemHealthDetail 
        isOpen={showSystemHealthDetail} 
        onClose={() => setShowSystemHealthDetail(false)} 
      />
    </div>
  );
};

export default OverviewRedesigned;
